import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom'
import { Loading } from '../../components/Loading';
import { MainButton } from '../../components/MainButton'
import { BookingHeader } from '../../components/BookingHeader';
import { ConfigType } from '../../contexts/AppConfigContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { usePluginspace } from '../../hooks/usePluginspace';
import './index.css'
import Swal from 'sweetalert2';
import api from '../../services/api';
import InputMask from "react-input-mask";
import { Checkbox } from '../../components/Checkbox';
import { cpfCnpjNoMask, datetimeToDateBR, telephoneNoMask } from '../../functions/mask';
import { getWeekDay } from '../../functions/util';
import { centsToFloat, floatToPrice } from '../../functions/price_functions';
import { Sparkles, Calendar, Clock, Users, User, MapPin, Phone, Mail, Check } from 'lucide-react';

type ImageType = {
    id: string;
    url: string;
}

type HourType = {
    id: string;
    hour: string;
}

type DayType = {
    able: boolean;
    day: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
    hours: HourType[];
}

type PriceGroupRange = {
    minPeople: number;
    maxPeople: number;
    price: number;
};

type EnvironmentType = {
    id: string;
    name: string;
    seatsAvailable: number;
    tables: number;
    pricePerSeat?: number | null;
    pricePerEnvironment?: number | null;
    pricePerTable?: number | null;
    priceByGroup?: PriceGroupRange[] | null;
}

type BookingConfigType = {
    id: string;
    able: boolean;
    dayConfirm: boolean;
    expiryTime: number;
    maxPeople: number;
    paymentConfirm: boolean;
    valuePaymentConfirm: number;
    days: DayType[];
    images: ImageType[];
    environment: EnvironmentType[];
}

type BookingToSendType = {
    date: string;
    bookingHourId: number;
    bookingEnvironmentId: number;
    email: string;
    bookingConfigId: number;
    people: number;
    name: string;
    phone: string;
    hour: string;
    // Dados de preço calculados pelo backend
    calculatedPrice?: number;
    priceInReais?: number;
    priceType?: string;
    priceBreakdown?: any;
}

type PlansType = {
    id: number;
    codePlan: number;
    installment: number;
}

type IntegrationType = {
    id: number;
    pluginconfigId: number;
    brand: string;
    cardOperator: number;
    codePay: string;
    nameGateway: string;
    paymentPlans: PlansType[];
    typePay: string;
}

type CardTotvsType = {
	amount: number,
	deliveryfee: number,
	installments: number | string,
	cardholderName: string,
	cardNumber: null,
	nameCardBrand: string
	expirationMonth: string,
	expirationYear: string,
	securityCode: string,
	brand: string,
	tokenizedPan: string,
    gatewayId?: number;
    plugintemplateId?: number;
    email?: string;
    cpf?: string;
    phone?: string;
    fingerprintSessionId?: string;
}

export function BookingPayment() {
    const { pluginspace_id } = useParams();
    const { registerAccess, getToken, pluginspaceData } = usePluginspace()
    const navigate = useNavigate();

    const { getAppConfig } = useAppConfig();
    const [plugintemplateId, setPlugintemplateId] = useState(0);
    const [config, setConfig] = useState<ConfigType | void>();
    const [loading, setLoading] = useState(true);
    const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(null);
    const [booking, setBooking] = useState<BookingToSendType | null>(null);
    const [name, setName] = useState('');
    const [number, setNumber] = useState('');
    const [cvv, setCvv] = useState('');
    const [cpf, setCpf] = useState('');
    const [brand, setBrand] = useState('');
    const [integrationBrands, setIntegrationBrands] = useState<IntegrationType[]>([])
    const [cardDate, setCardDate] = useState('');
    const [maskCard, setMaskCard] = useState('9999 9999 9999 99')
    const [maskCvv, setMaskCvv] = useState('999')
    const [calculatedPrice, setCalculatedPrice] = useState<number | null>(null)

    useEffect(() => {
        registerAccess('booking/payment')
        init();
    }, []);

    // Retorna o valor calculado que veio do backend via localStorage
    const getBookingPrice = (): number => {
        if (!booking) return 0;
        
        // Prioridade 1: Usar o preço calculado pelo backend (em centavos)
        if (booking.calculatedPrice !== undefined && booking.calculatedPrice !== null) {
            return booking.calculatedPrice;
        }
        
        // Fallback: Se por algum motivo não tiver o preço calculado, usar valuePaymentConfirm
        return bookingConfig?.valuePaymentConfirm || 0;
    };

    async function init() {
        setLoading(true);
        await getConfig()
        await getBookingConfig();
        getBooking();
        setLoading(false)
    }

    async function loadBrands(gatewayId: number){
        try {
            
            const resPluginspace = await api.get('/api/pluginspace/this', {
                headers: {
                    'Authorization': `Bearer ${await getToken()}`
                }
            });
            if(resPluginspace.data.data && resPluginspace.data.data[0] && resPluginspace.data.data[0].hash){
                const resIntegration = await api.post('api/orders/card-payment-get', 
					{
						hash: resPluginspace.data.data[0].hash,
					},
					{
						headers: {
							Authorization: `Bearer ${await getToken()}`,
						},
					}
				);
                
                const newBrandsIntegrations:IntegrationType[] = resIntegration.data.data.data.filter((integration:IntegrationType) => (integration.pluginconfigId === gatewayId && integration.brand !== 'Pix'))
                newBrandsIntegrations.map((brand) => {
                    if(brand.codePay.includes('ECOMMERCE')){
                        brand.codePay = `${brand.codePay}-${brand.brand}`
                    }
                })
                setIntegrationBrands(newBrandsIntegrations);
                setBrand(newBrandsIntegrations[0].codePay)
            }
        } catch (e) {
            console.log(e)
        }
    }

    async function getPlugintemplate(gatewayId: number) {
        await api
			.get(
				`/api/pluginconfig/${gatewayId}`,
				{
					headers: {
						Authorization: `Bearer ${await getToken()}`,
					},
				}
			)
			.then((response) => {
				if(response.data && response.data.data && response.data.data[0] && response.data.data[0].plugintemplateId){
                    setPlugintemplateId(response.data.data[0].plugintemplateId)
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro!',
                        text: 'Não foi possível encontrar forma de pagamento por cartão para esta loja.',
                    }).then(() => navigate(`/${pluginspace_id}/booking`))
                }
			})
			.catch((err) => {
				Swal.fire({
                    icon: 'error',
                    title: 'Erro!',
                    text: 'Não foi possível encontrar forma de pagamento por cartão para esta loja.',
                }).then(() => navigate(`/${pluginspace_id}/booking`))
			});
    }

    function getBooking() {
        const bookingStorage = localStorage.getItem('bookingToPay');
        if(bookingStorage){
            setBooking(JSON.parse(bookingStorage))
        } else {
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: 'Não foi possível encontrar a reserva.',
            }).then(() => navigate(`/${pluginspace_id}/booking`))
        }
    }

    async function getBookingConfig() {
        await api.get('/api/queueReservation/bookingConfig', {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        }).then((response) => {
            const bookingResponse = response.data;
            if(bookingResponse && bookingResponse.data && bookingResponse.data[0]){
                const bookingConfigItem = bookingResponse.data[0];
                if(bookingConfigItem && bookingConfigItem.able){
                    setBookingConfig(bookingConfigItem)
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Reserva não está habilitada!',
                        text: 'Loja não está com o esquema de reservas habilitada.',
                    }).then(() => navigate(`/${pluginspace_id}`))    
                }
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Reserva não configurada!',
                    text: 'Loja não está com o esquema de reserva configurada.',
                }).then(() => navigate(`/${pluginspace_id}`))
            }
        })
        .catch((err) => {
            console.log(err);
            Swal.fire({
                icon: 'error',
                title: 'Reserva não configurada!',
                text: 'Loja não está com o esquema de reservas configurada.',
            }).then(() => navigate(`/${pluginspace_id}`))
        })
    }

    async function encryptCard() {
        const paymentAmount = getBookingPrice();
        return await api
			.post(
				"/api/cryptography/encryptJson",
				{
					value: {
						amount: paymentAmount,
						cardholderName: name,
						cardNumber: number,
						securityCode: cvv.split("_").join(""),
                        installments: 1,
						expirationMonth: parseInt(cardDate.split("/")[0]),
						expirationYear: parseInt(cardDate.split("/")[1]),
                        gatewayId: config?.content.pdv.gatewayId,
                        plugintemplateId,
                        email: booking?.email,
                        cpf: cpfCnpjNoMask(cpf),
                        phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
                        nameCardBrand: brand,
					},
				},
				{
					headers: {
						Authorization: `Bearer ${await getToken()}`,
					},
				}
			)
			.then((response) => {
				return response.data.data.encryptedData;
			})
			.catch((err) => {
				console.log(err);
				return null;
			});
    }

    async function pagseguroCardData() {
		return await api
			.get("/api/pagseguro/pagseguroPublicKey", {
				headers: {
					Authorization: `Bearer ${await getToken()}`,
				},
			})
			.then(async (response) => {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				const encryptedCard = PagSeguro.encryptCard({
					publicKey: response.data.publicKey,
					holder: name,
					number: number.split(" ").join(""),
					expMonth: parseInt(cardDate.split("/")[0]),
					expYear: parseInt(cardDate.split("/")[1]),
					securityCode: cvv.split("_").join(""),
                    nameCardBrand: brand,
				});
				return encryptedCard;
			})
			.catch((err) => {
				console.log(err);
				return null;
			});
	}

    async function encryptCardPagseguro() {
		const encryptedCard = await pagseguroCardData();
		const paymentAmount = getBookingPrice();
		return await api
			.post(
				"/api/cryptography/encryptJson",
				{
					value: {
						amount: paymentAmount,
						cardNumber: number.split(" ").join(""),
						cardholderName: name,
						securityCode: cvv.split("_").join(""),
						expirationMonth: parseInt(cardDate.split("/")[0]),
						expirationYear: parseInt(cardDate.split("/")[1]),
						encryptedCard: encryptedCard.encryptedCard,
                        gatewayId: config?.content.pdv.gatewayId,
                        plugintemplateId,
                        email: booking?.email,
                        cpf: cpfCnpjNoMask(cpf),
                        phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
                        installments: 1,
					},
				},
				{
					headers: {
						Authorization: `Bearer ${await getToken()}`,
					},
				}
			)
			.then((response) => {
				return response.data.data.encryptedData;
			})
			.catch((err) => {
				console.log(err);
				return null;
			});
	}

    async function setTokenizedCard(response: any) {

		if (response.tokenizedPan) {
			setLoading(true);
			const paymentAmount = getBookingPrice();
			const cardTotvs: CardTotvsType = {
				amount: paymentAmount,
				deliveryfee: 0,
				brand: response.brand,
				cardholderName: name,
				cardNumber: null,
				expirationMonth: cardDate.split("/")[0],
				expirationYear: cardDate.split("/")[1],
				installments: 1,
				nameCardBrand: response.brandName,
				securityCode: cvv.split("_").join(""),
				tokenizedPan: response.tokenizedPan,
                fingerprintSessionId: response.fingerprintSessionId,
                gatewayId: config?.content.pdv.gatewayId,
                plugintemplateId,
                email: booking?.email,
                cpf: cpfCnpjNoMask(cpf),
                phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
			};
			const encryptedData = await encryptCardTotvs(cardTotvs);
            const subscriptionStorage = localStorage.getItem(window.btoa('subscription_notification'))        
            let subscription = null;
            if(subscriptionStorage && window.atob(subscriptionStorage) && JSON.parse(window.atob(subscriptionStorage))){
                subscription = JSON.parse(window.atob(subscriptionStorage))
            }
			await api.post('/api/queueReservation/booking', {
                date: booking?.date,
                bookingHourId: booking?.bookingHourId,
                bookingEnvironmentId: booking?.bookingEnvironmentId,
                email: booking?.email,
                bookingConfigId: bookingConfig?.id ? bookingConfig.id : 0,
                people: booking?.people,
                name: booking?.name,
                phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
                payment: encryptedData,
                subscription,
                urlNotification: window.location.href.replace("payment", telephoneNoMask(booking?.phone ? booking.phone : ''))
            },
            {
                headers: {
                    Authorization: `Bearer ${await getToken()}`,
                },
            }).then((response) => {
                setLoading(false);
                const bookingResponse = response.data;
                if(bookingResponse.success){
                    const phone = telephoneNoMask(booking?.phone ? booking.phone : '');
                    const redirectUrl = `/${pluginspace_id}/booking/success/${phone}`;
                    console.log('Redirecionando para:', redirectUrl);
                    console.log('Phone original:', booking?.phone);
                    console.log('Phone sem máscara:', phone);
                    console.log('Pluginspace ID:', pluginspace_id);
                    
                    try {
                        console.log('BookingPayment: navegando para:', redirectUrl);
                        navigate(redirectUrl);
                    } catch (e) {
                        console.error('BookingPayment: erro ao chamar navigate():', e);
                    }
                    Swal.fire({
                        icon: 'success',
                        title: 'Sucesso!',
                        text: 'Você realizou sua reserva.',
                    });
                    setTimeout(() => {
                        if (!window.location.pathname.includes('/booking/success')) {
                            console.warn('BookingPayment: navigate falhou — forçando window.location.replace ->', redirectUrl);
                            window.location.replace(redirectUrl);
                        }
                    }, 600);
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro ao realizar reserva!',
                        text: 'Não foi possível realizar reserva.',
                    })    
                }
            })
            .catch((err) => {
                setLoading(false);
                let title = 'Erro ao realizar reserva!';
                let text = 'Não foi possível realizar reserva.';
                if(err && err.response && err.response.data && err.response.data.error){
                    title = err.response.data.error;
                }
                if(err && err.response && err.response.data && err.response.data.message){
                    text = err.response.data.message;
                }
                Swal.fire({
                    icon: 'error',
                    title,
                    text,
                })
            })
		} else {
			setLoading(false);
			Swal.fire({
				icon: 'error',
				title: 'Erro',
				text: 'Não foi possível finalizar sua compra',
			})
		}
	}

    async function encryptCardTotvs(cardTotvs: CardTotvsType) {
		return await api.post('api/cryptography/encryptJson',
			{
				value: cardTotvs
			},
			{
				headers: {
					'Authorization': `Bearer ${await getToken()}`
				}
			}).then((response) => {
				return response.data.data.encryptedData
			})
			.catch((err) => {
				console.log(err);
				return null;
			})
	}

    async function finalizeOrderCardTotvs() {
        await api
        .get(`/api/pluginconfig/${config?.content.pdv.gatewayId}`, {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        })
        .then(async (response) => {
            const dataPlugin = response.data;
            
            if (dataPlugin.data && dataPlugin.data[0] && dataPlugin.data[0].content) {
                const configTotvs = dataPlugin.data[0].content;
                const tkData = await api
                    .post(`/api/totvshub/accessTotvs`,
                        {
                            gatewayId: config?.content.pdv.gatewayId,
                            pluginspaceId: pluginspaceData?.id ? pluginspaceData?.id : 1
                        },
                        {
                            headers: {
                                Authorization: `Bearer ${await getToken()}`,
                            },
                        })
                if (tkData.data && tkData.data.success && tkData.data.tokenData && tkData.data.tokenData.access_token) {
                    const paymentAmount = getBookingPrice();
                    // @ts-ignore
                    clientData = {
                        amount: paymentAmount,
                        currency: "BRL",
                        cardType: "CreditCard",
                        externalPosId: configTotvs.externalposid,
                        externalBusinessUnitId: configTotvs.externalbusinessunitid,
                        externalTransactionId: configTotvs.externaltransactionid,
                        installments: 1,
                        card: {
                            expirationYear: parseInt(cardDate.split("/")[0]),
                            expirationMonth: parseInt(cardDate.split("/")[1]),
                            securityCode: cvv.split("_").join(""),
                            number: number,
                        },
                        getTokenInfoAPI: {
                            // uri: 'https://dev.raas.varejo.totvs.com.br/pay-hub/transacting/card/api/payment-data',
                            uri: configTotvs.urlGetPaymentData,
                            token: tkData.data.tokenData.access_token,
                        }
                    };
                    
                    // @ts-ignore
                    getTokenizedCardPan(clientData, setTokenizedCard);
                } else {
                    setLoading(false)
                    Swal.fire({
                        icon: 'error',
                        title: 'Erro',
                        text: 'Não foi possível finalizar sua compra',
                    })
                    return null
                }
            } else {
                setLoading(false)
                Swal.fire({
                    icon: 'error',
                    title: 'Erro',
                    text: 'Não foi possível finalizar sua compra',
                })
                return null
            }
        })
        .catch((err) => {
            setLoading(false)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Não foi possível finalizar sua compra',
            })
            return null;
        });
    }

    async function goBooking() {
        
        if(!bookingConfig?.id){
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: `Erro ao encontrar configuração de reserva`,
            })
            return;
        }

        if(!name || !number || number.includes("_") || !cardDate || cardDate.includes("_") || !cvv){
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: `Verifique se preencheu todos os dados corretamente`,
            })
            return;
        }

        if(!config?.content.pdv.gatewayId){
            Swal.fire({
                icon: 'error',
                title: 'Erro!',
                text: `Loja não possui meios de pagamento com cartão.`,
            })
            return;
        }
        
        setLoading(true);
        
        let payment = '';
        if(plugintemplateId === 10){
            payment = await encryptCardPagseguro()
        } else if(plugintemplateId === 25){
            await finalizeOrderCardTotvs()
            return
        } else {
            payment = await encryptCard()
        }
        const subscriptionStorage = localStorage.getItem(window.btoa('subscription_notification'))        
        let subscription = null;
        if(subscriptionStorage && window.atob(subscriptionStorage) && JSON.parse(window.atob(subscriptionStorage))){
            subscription = JSON.parse(window.atob(subscriptionStorage))
        }
        await api.post('/api/queueReservation/booking', {
            date: booking?.date,
            bookingHourId: booking?.bookingHourId,
            bookingEnvironmentId: booking?.bookingEnvironmentId,
            email: booking?.email,
            bookingConfigId: bookingConfig.id,
            people: booking?.people,
            name: booking?.name,
            phone: telephoneNoMask(booking?.phone ? booking.phone : ''),
            payment: payment,
            subscription,
            urlNotification: window.location.href.replace("payment", telephoneNoMask(booking?.phone ? booking.phone : ''))
        },
        {
            headers: {
                Authorization: `Bearer ${await getToken()}`,
            },
        }).then((response) => {
            setLoading(false);
            const bookingResponse = response.data;
            if(bookingResponse.success){
                const phone = telephoneNoMask(booking?.phone ? booking.phone : '');
                const redirectUrl = `/${pluginspace_id}/booking/success/${phone}`;
                console.log('Redirecionando para (segunda função):', redirectUrl);
                console.log('Phone original (segunda função):', booking?.phone);
                console.log('Phone sem máscara (segunda função):', phone);
                console.log('Pluginspace ID (segunda função):', pluginspace_id);
                
                Swal.fire({
                        icon: 'success',
                        title: 'Sucesso!',
                        text: 'Você realizou sua reserva.',
                    }).then(() => {
                        try {
                            console.log('BookingPayment (segunda função): navegando para:', redirectUrl);
                            navigate(redirectUrl);
                        } catch (e) {
                            console.error('BookingPayment (segunda função): erro ao chamar navigate():', e);
                        }
                        setTimeout(() => {
                            if (!window.location.pathname.includes('/booking/success')) {
                                console.warn('BookingPayment (segunda função): navigate falhou — forçando window.location.href ->', redirectUrl);
                                window.location.href = redirectUrl;
                            }
                        }, 600);
                    })
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Erro ao realizar reserva!',
                    text: 'Não foi possível realizar reserva.',
                })    
            }
        })
        .catch((err) => {
            setLoading(false);
            let title = 'Erro ao realizar reserva!';
            let text = 'Não foi possível realizar reserva.';
            if(err && err.response && err.response.data && err.response.data.error){
                title = err.response.data.error;
            }
            if(err && err.response && err.response.data && err.response.data.message){
                text = err.response.data.message;
            }
            Swal.fire({
                icon: 'error',
                title,
                text,
            })
        })
        
    }

    async function getConfig(){
        const findConfig = await getAppConfig()
        setConfig(findConfig);
        if(findConfig){
            await loadBrands(findConfig.content.pdv.gatewayId);
            await getPlugintemplate(findConfig.content.pdv.gatewayId);
        }
    }

    return (
        <>
            <BookingHeader />
            <div style={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                padding: '40px 24px',
                overflowX: 'hidden',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                { loading ?
                    <div style={{
                        position: 'fixed',
                        top: '80px',
                        left: 0,
                        right: 0,
                        height: 'calc(100vh - 80px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(248, 249, 250, 0.95)',
                        zIndex: 9999
                    }}>
                        <Loading
                            type="spinningBubbles"
                            color="#6366f1"
                            width={64}
                            height={64}
                        />
                    </div>
                :
                <div style={{
                    maxWidth: '800px',
                    margin: '0 auto',
                    width: '100%'
                }}>
                { bookingConfig && (
                    <div>
                        {/* Step Indicator / Wizard */}
                        <div style={{
                            width: '100%',
                            maxWidth: '500px',
                            margin: '0 auto 40px',
                            padding: '0 20px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: (booking?.priceType && booking.priceType !== 'free') ? 'space-between' : 'space-around',
                                position: 'relative',
                                padding: '0 40px'
                            }}>
                                {/* Progress Line Background */}
                                <div style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: (booking?.priceType && booking.priceType !== 'free') ? '60px' : '25%',
                                    right: (booking?.priceType && booking.priceType !== 'free') ? '86px' : '25%',
                                    height: '2px',
                                    background: 'rgba(0, 0, 0, 0.1)',
                                    zIndex: 0
                                }}></div>
                                
                                {/* Progress Line Active */}
                                <div style={{
                                    position: 'absolute',
                                    top: '20px',
                                    left: (booking?.priceType && booking.priceType !== 'free') ? '60px' : '25%',
                                    width: (booking?.priceType && booking.priceType !== 'free') ? '33%' : '0%',
                                    height: '2px',
                                    background: 'linear-gradient(135deg, #4338ca 0%, #764ba2 100%)',
                                    zIndex: 1,
                                    transition: 'width 0.5s ease'
                                }}></div>

                                {/* Step 1: Dados */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    zIndex: 2,
                                    cursor: 'pointer'
                                }}
                                onClick={() => navigate(`/${pluginspace_id}/booking`)}
                                >
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(135deg, #4338ca 0%, #764ba2 100%)',
                                        border: '2px solid transparent',
                                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                        transition: 'all 0.3s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                    >
                                        <svg style={{ width: '20px', height: '20px', color: '#fff' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <span style={{
                                        marginTop: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: '#4338ca',
                                        fontFamily: "'Poppins', sans-serif"
                                    }}>Dados</span>
                                </div>

                                {/* Step 2: Pagamento - Apenas se não for gratuito */}
                                {(booking?.priceType && booking.priceType !== 'free') && (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        zIndex: 2
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: '#fff',
                                            border: '2px solid #4338ca',
                                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                            transition: 'all 0.3s'
                                        }}>
                                            <svg style={{ width: '20px', height: '20px', color: '#667eea' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                            </svg>
                                        </div>
                                        <span style={{
                                            marginTop: '8px',
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: '#4338ca',
                                            fontFamily: "'Poppins', sans-serif"
                                        }}>Pagamento</span>
                                    </div>
                                )}

                                {/* Step 3: Sucesso */}
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    zIndex: 3
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(0, 0, 0, 0.05)',
                                        border: '2px solid rgba(0, 0, 0, 0.1)',
                                        transition: 'all 0.3s'
                                    }}>
                                        <svg style={{ width: '20px', height: '20px', color: '#999' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span style={{
                                        marginTop: '8px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        color: '#999',
                                        fontFamily: "'Poppins', sans-serif"
                                    }}>Sucesso</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Título da Página */}
                        <div style={{
                            textAlign: 'center',
                            marginBottom: '40px'
                        }}>
                            <h1 style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: 'clamp(32px, 5vw, 48px)',
                                fontWeight: '700',
                                background: 'linear-gradient(135deg, #4338ca 0%, #764ba2 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                marginBottom: '12px',
                                letterSpacing: '-0.5px'
                            }}>
                                Pagamento
                            </h1>
                            <p style={{
                                fontFamily: "'Poppins', sans-serif",
                                fontSize: 'clamp(16px, 2.5vw, 20px)',
                                color: '#6c757d',
                                fontWeight: '400',
                                letterSpacing: '0.3px'
                            }}>
                                Complete o pagamento para confirmar sua reserva
                            </p>
                        </div>
                        
                        {/* Card de Resumo da Reserva */}
                        { booking && (
                            <div
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(99, 102, 241, 0.22), 0 8px 16px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                                }}
                                style={{
                                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.08) 50%, rgba(67, 56, 202, 0.1) 100%)',
                                    backdropFilter: 'blur(20px)',
                                    WebkitBackdropFilter: 'blur(20px)',
                                    borderRadius: '20px',
                                    padding: '32px',
                                    marginBottom: '20px',
                                    boxShadow: '0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                                    border: '1px solid rgba(99, 102, 241, 0.25)',
                                    position: 'relative' as const,
                                    overflow: 'hidden',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
                                    pointerEvents: 'none'
                                }}></div>
                                <div style={{ position: 'relative', zIndex: 1 }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        marginBottom: '20px'
                                    }}>
                                        <Sparkles 
                                            size={28}
                                            style={{
                                                color: "#4338ca"
                                            }} 
                                        />
                                        <h3 style={{
                                            fontSize: '22px',
                                            fontWeight: '600',
                                            color: "#4338ca",
                                            margin: 0,
                                            fontFamily: "'Poppins', sans-serif"
                                        }}>
                                            Resumo da Reserva
                                        </h3>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '12px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Calendar size={16} style={{ color: '#4338ca' }} />
                                                <span style={{ fontWeight: '600', color: '#4338ca', fontFamily: "'Poppins', sans-serif" }}>Data:</span>
                                            </div>
                                            <span style={{ color: '#2d3436', fontFamily: "'Poppins', sans-serif" }}>{datetimeToDateBR(booking.date)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Clock size={16} style={{ color: '#4338ca' }} />
                                                <span style={{ fontWeight: '600', color: '#4338ca', fontFamily: "'Poppins', sans-serif" }}>Horário:</span>
                                            </div>
                                            <span style={{ color: '#2d3436', fontFamily: "'Poppins', sans-serif" }}>{booking.hour}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Users size={16} style={{ color: '#4338ca' }} />
                                                <span style={{ fontWeight: '600', color: '#4338ca', fontFamily: "'Poppins', sans-serif" }}>Pessoas:</span>
                                            </div>
                                            <span style={{ color: '#2d3436', fontFamily: "'Poppins', sans-serif" }}>{booking.people} {booking.people > 1 ? 'pessoas' : 'pessoa'}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <User size={16} style={{ color: '#4338ca' }} />
                                                <span style={{ fontWeight: '600', color: '#4338ca', fontFamily: "'Poppins', sans-serif" }}>Nome:</span>
                                            </div>
                                            <span style={{ color: '#2d3436', fontFamily: "'Poppins', sans-serif" }}>{booking.name}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Phone size={16} style={{ color: '#4338ca' }} />
                                                <span style={{ fontWeight: '600', color: '#4338ca', fontFamily: "'Poppins', sans-serif" }}>Telefone:</span>
                                            </div>
                                            <span style={{ color: '#2d3436', fontFamily: "'Poppins', sans-serif" }}>{booking.phone}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Mail size={16} style={{ color: '#4338ca' }} />
                                                <span style={{ fontWeight: '600', color: '#4338ca', fontFamily: "'Poppins', sans-serif" }}>Email:</span>
                                            </div>
                                            <span style={{ color: '#2d3436', fontFamily: "'Poppins', sans-serif" }}>{booking.email}</span>
                                        </div>
                                    </div>

                                    {/* Breakdown de Preço */}
                                    {booking.priceType && booking.priceType !== 'free' && (
                                        <div style={{
                                            marginTop: '20px',
                                            padding: '15px',
                                            background: 'rgba(255, 255, 255, 0.15)',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(255, 255, 255, 0.25)'
                                        }}>
                                            <h4 style={{
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                marginBottom: '10px',
                                                background: 'linear-gradient(135deg, #4338ca 0%, #764ba2 100%)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                backgroundClip: 'text'
                                            }}>
                                                Detalhes do Valor
                                            </h4>
                                            <div style={{
                                                fontSize: '18px',
                                                fontWeight: '700',
                                                marginBottom: '10px',
                                                color: '#4338ca'
                                            }}>
                                                Valor Total: {floatToPrice(centsToFloat(getBookingPrice()))}
                                            </div>
                                            
                                            {booking.priceType === 'byGroup' && booking.priceBreakdown?.rangeApplied && (
                                                <div style={{ fontSize: '14px', color: '#4338ca' }}>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • Tipo: <strong>Preço por Grupo</strong>
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • Faixa aplicada: {booking.priceBreakdown.rangeApplied.minPeople} a {booking.priceBreakdown.rangeApplied.maxPeople} pessoas
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • Pessoas na reserva: {booking.people}
                                                    </p>
                                                </div>
                                            )}
                                            
                                            {booking.priceType === 'perSeat' && booking.priceBreakdown?.pricePerUnit && (
                                                <div style={{ fontSize: '14px', color: '#4338ca' }}>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • Tipo: <strong>Preço por Pessoa</strong>
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • {booking.people} pessoa(s) × {floatToPrice(centsToFloat(booking.priceBreakdown.pricePerUnit))}
                                                    </p>
                                                </div>
                                            )}
                                            
                                            {booking.priceType === 'perEnvironment' && (
                                                <div style={{ fontSize: '14px', color: '#4338ca' }}>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • Tipo: <strong>Preço Fixo do Ambiente</strong>
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • Valor único independente do número de pessoas
                                                    </p>
                                                </div>
                                            )}
                                            
                                            {booking.priceType === 'perTable' && booking.priceBreakdown?.tables && (
                                                <div style={{ fontSize: '14px', color: '#667eea' }}>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • Tipo: <strong>Preço por Mesa</strong>
                                                    </p>
                                                    <p style={{ margin: '5px 0' }}>
                                                        • {booking.priceBreakdown.tables} mesa(s) × {floatToPrice(centsToFloat(booking.priceBreakdown.pricePerUnit))}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Mensagem Informativa */}
                                    <div style={{
                                        marginTop: '20px',
                                        padding: '20px',
                                        background: 'rgba(99, 102, 241, 0.1)',
                                        borderRadius: '12px',
                                        border: '1px solid rgba(99, 102, 241, 0.2)',
                                        fontSize: '13px',
                                        color: '#2d3436',
                                        lineHeight: '1.7',
                                        textAlign: 'justify' as const,
                                        fontFamily: "'Poppins', sans-serif",
                                        letterSpacing: '0.3px'
                                    }}>
                                        <p style={{ 
                                            margin: '0 0 12px 0',
                                            fontWeight: '500'
                                        }}>
                                            Para confirmar sua reserva, o restaurante utiliza uma pré-cobrança de <strong style={{ color: '#6366f1' }}>{floatToPrice(centsToFloat(getBookingPrice()))}</strong>.
                                        </p>
                                        
                                        <p style={{ 
                                            margin: '0 0 12px 0' 
                                        }}>
                                            O valor só será cobrado em caso de não comparecimento na data e horário reservados.
                                        </p>
                                        
                                        <p style={{ 
                                            margin: '0 0 12px 0' 
                                        }}>
                                            Caso você compareça, o valor não será cobrado ou poderá ser utilizado como consumação, conforme a política do restaurante.
                                        </p>
                                        
                                        <p style={{ 
                                            margin: '0',
                                            fontWeight: '500',
                                            color: '#4f46e5'
                                        }}>
                                            Cancelamentos dentro do prazo não geram cobrança.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Card de Dados do Pagamento */}
                        <div
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-3px)';
                                e.currentTarget.style.boxShadow = '0 12px 32px rgba(99, 102, 241, 0.22), 0 8px 16px rgba(99, 102, 241, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)';
                            }}
                            style={{
                                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(79, 70, 229, 0.08) 50%, rgba(67, 56, 202, 0.1) 100%)',
                                backdropFilter: 'blur(20px)',
                                WebkitBackdropFilter: 'blur(20px)',
                                borderRadius: '20px',
                                padding: '32px',
                                marginBottom: '16px',
                                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.15), 0 4px 12px rgba(99, 102, 241, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)',
                                border: '1px solid rgba(99, 102, 241, 0.25)',
                                position: 'relative' as const,
                                overflow: 'hidden',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%)',
                                pointerEvents: 'none'
                            }}></div>
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '25px'
                                }}>
                                    <i className="fa fa-credit-card" 
                                        style={{
                                            fontSize: '28px',
                                            color: '#4338ca'
                                        }}
                                    />
                                    <h3 style={{
                                        fontSize: '22px',
                                        fontWeight: '600',
                                        color: '#4338ca',
                                        margin: 0,
                                        fontFamily: "'Poppins', sans-serif"
                                    }}>
                                        Dados do Pagamento
                                    </h3>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px'
                                }}>
                                    {/* Nome Completo + CPF (mesma linha) */}
                                    <div className="name-cpf-row" style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr minmax(calc(14ch + 2rem), 180px)',
                                        gap: '8px',
                                        alignItems: 'start'
                                    }}>
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontFamily: "'Poppins', sans-serif",
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#4338ca',
                                                letterSpacing: '0.3px'
                                            }}>
                                                Nome Completo
                                            </label>
                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(event) => setName(event.target.value)}
                                                placeholder="Informe o nome como está no cartão"
                                                className="payment-input"
                                            />
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#4338ca',
                                                marginTop: '6px',
                                                fontFamily: "'Poppins', sans-serif",
                                                fontStyle: 'italic',
                                                opacity: 0.9
                                            }}>
                                                Nome deve ser preenchido igual está no cartão
                                            </div>
                                        </div>

                                        {/* CPF (linha do Nome) - espaço para ~12 caracteres */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontFamily: "'Poppins', sans-serif",
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#4338ca',
                                                letterSpacing: '0.3px'
                                            }}>
                                                CPF
                                            </label>
                                            <InputMask
                                                mask={'999.999.999-99'}
                                                type="text"
                                                value={cpf}
                                                onChange={(event) => setCpf(event.target.value)}
                                                maxLength={15}
                                                placeholder="000.000.000-00"
                                                className="cpf-input"
                                            />
                                        </div>
                                    </div>
                                    {/* CPF, Número do Cartão, Validade e CVV (mesma linha) */}
                                    <div className="cpf-brand-row" style={{
                                        display: 'grid',
                                        // 4 colunas: Número do Cartão | Validade | CVV | Bandeira
                                        // Ajuste: aumentar Validade em ~2 caracteres e diminuir CVV em ~2
                                        // (aproxima visualmente os campos e mantém Bandeira maior).
                                        // Aumenta espaço mínimo e máximo para Bandeira para evitar corte
                                        // Ajuste final: ceder mais 2 caracteres ao Número do Cartão
                                        // aumentando seu maxWidth e reduzindo levemente a coluna Bandeira.
                                        // Pequeno ajuste final: ceder +1 caractere (~+10px) ao campo Número do Cartão
                                        gridTemplateColumns: 'minmax(140px, 320px) 110px 60px minmax(160px, 440px)',
                                        gap: '6px',
                                        alignItems: 'center'
                                    }}>
                                        {/* Número do Cartão */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontFamily: "'Poppins', sans-serif",
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                marginBottom: '8px',
                                                color: '#4338ca',
                                                letterSpacing: '0.3px'
                                            }}>
                                                Número do Cartão
                                            </label>
                                            <InputMask
                                                mask={maskCard}
                                                type="text"
                                                value={number}
                                                onChange={(event) => setNumber(event.target.value)}
                                                onFocus={(e: any) => {
                                                    setMaskCard('9999 9999 9999 9999');
                                                }}
                                                onBlur={(e: any) => {
                                                    if(number.split("_").join("").split(" ").join("").length === 14) setMaskCard('9999 9999 9999 99');
                                                    else if(number.split("_").join("").split(" ").join("").length === 15) setMaskCard('9999 9999 9999 999');
                                                    else setMaskCard('9999 9999 9999 9999');
                                                }}
                                                placeholder="0000 0000 0000 0000"
                                                className="payment-input card-number-input"
                                            />
                                                </div>

                                                {/* Validade */}
                                                <div>
                                                    <label style={{
                                                        display: 'block',
                                                        fontFamily: "'Poppins', sans-serif",
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        marginBottom: '8px',
                                                        color: '#4338ca',
                                                        letterSpacing: '0.3px'
                                                    }}>
                                                        Validade
                                                    </label>
                                                    <InputMask
                                                        mask={'99/9999'}
                                                        type="text"
                                                        value={cardDate}
                                                        onChange={(event) => {
                                                            setCardDate(event.target.value)
                                                        }}
                                                        placeholder="MM/AAAA"
                                                        className="payment-input"
                                                    />
                                                </div>

                                                {/* CVV */}
                                                <div>
                                                    <label style={{
                                                        display: 'block',
                                                        fontFamily: "'Poppins', sans-serif",
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        marginBottom: '8px',
                                                        color: '#4338ca',
                                                        letterSpacing: '0.3px'
                                                    }}>
                                                        CVV
                                                    </label>
                                                    <InputMask
                                                        mask={maskCvv}
                                                        onBeforeInputCapture={(event) => {
                                                            if(event.currentTarget.value.split("_").join("").length >= 3) setMaskCvv('9999')
                                                            else setMaskCvv('999')
                                                        }}
                                                        type="text"
                                                        value={cvv}
                                                        onChange={(event) => {
                                                            if(event.target.value.includes('_')) setMaskCvv('999')
                                                            setCvv(event.target.value)
                                                        }}
                                                        placeholder="000"
                                                        className="payment-input"
                                                    />
                                                </div>

                                                {/* Bandeira (agora na mesma linha, após CVV) */}
                                                <div>
                                                    <label style={{
                                                        display: 'block',
                                                        fontFamily: "'Poppins', sans-serif",
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        marginBottom: '8px',
                                                        color: '#4338ca',
                                                        letterSpacing: '0.3px'
                                                    }}>
                                                        Bandeira
                                                    </label>
                                                    <select
                                                        value={brand}
                                                        onChange={(event) => setBrand(event.target.value)}
                                                        className="brand-select"
                                                    >
                                                        {integrationBrands.map((payment) => (
                                                            <option key={payment.brand} value={payment.brand.toLowerCase()}>
                                                                {payment.brand.toUpperCase()}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                    
                                    <button
                                        onClick={() => goBooking()}
                                        onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = "translateY(-3px)";
                                        e.currentTarget.style.boxShadow = "0 12px 32px rgba(102, 126, 234, 0.4), 0 8px 16px rgba(102, 126, 234, 0.3)";
                                        }}
                                        onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "0 4px 16px rgba(102, 126, 234, 0.3)";
                                        }}
                                        style={{
                                            width: "100%",
                                            padding: "16px 24px",
                                            marginTop: "24px",
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            border: "none",
                                            borderRadius: "16px",
                                            color: "#ffffff",
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: "700",
                                            fontSize: "16px",
                                            letterSpacing: "0.5px",
                                            cursor: "pointer",
                                            boxShadow: "0 4px 16px rgba(102, 126, 234, 0.3)",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            textTransform: "uppercase",
                                            position: "relative" as const,
                                            overflow: "hidden"
                                        }}
                                    >
                                        <span style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                                            <Check size={20} strokeWidth={3} />
                                            Confirmar Reserva
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                </div>
                }
            </div>
        </>
    )
}